import { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import { ContractorScorecardFull } from "@/components/ContractorScorecard";
import { ReliabilityBadge } from "@/components/ReliabilityBadge";
import { PayProfileCard } from "@/components/contractor/PayProfileCard";
import { PayProfileForm } from "@/components/contractor/PayProfileForm";
import { ContractorMarketplacePanel } from "@/components/contractor/ContractorMarketplacePanel";
import { ContractorOfferHistoryPanel } from "@/components/contractor/ContractorOfferHistoryPanel";
import { getPayProfile } from "@/data/contractorPayProfile";
import { ContractorService } from "@/services/contractor.service";
import { getCrewsForContractor, getCrewMetrics } from "@/data/contractor/crew";
import { getJobCompletionDate } from "@/data/contractor/commandCenter";
import {
  getComplianceStatus,
  type ComplianceRequirementKey,
  type RequirementResolution,
  type RequirementStatus,
} from "@/data/contractor/compliance/complianceStatus";
import { hasRequiredAgreementSignature } from "@/data/contractor/onboarding/onboardingAgreements";
import { toast } from "sonner";
import { Contractor } from "@/data/types";
import {
  ArrowLeft, Phone, Mail, MapPin, Star, Briefcase, AlertTriangle,
  CheckCircle, XCircle, FileText, Clock, Shield, Wrench, Users,
  DollarSign, Send,
} from "lucide-react";
import type { JobStage } from "@/data/types";


function RatingStars({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`h-3.5 w-3.5 ${s <= Math.round(rating) ? "text-warning fill-warning" : "text-muted/40"}`} />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{rating}</span>
    </div>
  );
}

function getJobDisplayRef(job: { jobNumber?: string | number | null; id: string }): string {
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) return job.jobNumber.trim();
  return job.id;
}

function isInsuranceCurrent(expiry?: string): boolean {
  if (!expiry) return false;
  return expiry >= new Date().toISOString().slice(0, 10);
}

const PROFILE_COMPLIANCE_LABELS: Record<ComplianceRequirementKey, string> = {
  license: "License",
  insurance: "Insurance",
  w9: "W-9",
  background_check: "Background Check",
  agreement: "Agreement",
  banking_verification: "Payout Verification",
  certification: "Certification",
};

function getRequirementLabel(requirement: ComplianceRequirementKey): string {
  return PROFILE_COMPLIANCE_LABELS[requirement] ?? requirement;
}

function getRequirementStatusLabel(status: RequirementStatus): string {
  switch (status) {
    case "ok":
      return "Verified";
    case "pending_verification":
      return "Pending review";
    case "expiring_soon":
      return "Expiring soon";
    case "not_required":
      return "Not required";
    case "expired":
      return "Expired";
    case "rejected":
      return "Rejected";
    case "missing":
    default:
      return "Missing";
  }
}

function getRequirementStatusTone(status: RequirementStatus): string {
  switch (status) {
    case "ok":
      return "text-success";
    case "pending_verification":
    case "expiring_soon":
      return "text-warning";
    case "not_required":
      return "text-muted-foreground";
    case "expired":
    case "rejected":
    case "missing":
    default:
      return "text-destructive";
  }
}

function getRequirementStatusIcon(status: RequirementStatus) {
  if (status === "ok") return <CheckCircle className="h-4 w-4 text-success" />;
  if (status === "pending_verification" || status === "expiring_soon") {
    return <AlertTriangle className="h-4 w-4 text-warning" />;
  }
  if (status === "not_required") return <CheckCircle className="h-4 w-4 text-muted-foreground" />;
  return <XCircle className="h-4 w-4 text-destructive" />;
}

function getRequirementDetail(item: RequirementResolution): string | undefined {
  if (item.expiresAt) return `Expires ${item.expiresAt.slice(0, 10)}`;
  if (item.inheritedFromParent) return "Inherited from parent";
  return undefined;
}

function formatDisplayDateTime(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function getEffectiveStage(job: { currentStage: JobStage; id: string }, offerStatusByJobId: Map<string, string>): JobStage {
  const offerStatus = offerStatusByJobId.get(job.id);
  if (offerStatus === "accepted" && (job.currentStage === "Sale Closed" || job.currentStage === "Review and Validation")) {
    return "Assigned and Dispatched";
  }
  return job.currentStage;
}

function normalizeIdentity(value?: string): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function ContractorCrewsSection({ contractorId }: { contractorId: string }) {
  const { crews, jobs } = useAppData();
  const myCrews = useMemo(() => getCrewsForContractor(crews, contractorId), [crews, contractorId]);
  const rows = useMemo(() =>
    myCrews.map((crew) => ({
      crew,
      metrics: getCrewMetrics({
        crewId: crew.id,
        jobs,
        getCompletionDate: getJobCompletionDate,
        getJobQaCounts: (job) => {
          const done = getJobCompletionDate(job);
          if (!done) return { passed: 0, failed: 0 };
          return job.issueFlag ? { passed: 0, failed: 1 } : { passed: 1, failed: 0 };
        },
      }),
    })),
  [myCrews, jobs]);

  if (myCrews.length === 0) return null;

  return (
    <Card className="shadow-none">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Crews</h2>
        </div>
        <div className="space-y-2">
          {rows.map(({ crew, metrics }) => (
            <div key={crew.id} className="rounded-lg border border-border bg-muted/10 p-3">
              <div className="flex items-start justify-between gap-2 mb-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{crew.name}</p>
                  <p className="text-[11px] text-muted-foreground">Lead: {crew.leadInstallerName}</p>
                </div>
                <Badge variant="outline" className={`text-[9px] shrink-0 ${crew.active ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground"}`}>
                  {crew.active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2.5 border-t border-border/50">
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Completed</p>
                  <p className="text-sm font-semibold tabular-nums mt-0.5">{metrics.completedCount}</p>
                </div>
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">QA Pass</p>
                  {metrics.qaPassRate == null
                    ? <Badge variant="outline" className="mt-0.5 text-[9px] bg-muted text-muted-foreground">Not enough data</Badge>
                    : <p className="text-sm font-semibold tabular-nums mt-0.5">{Math.round(metrics.qaPassRate * 100)}%</p>}
                </div>
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider">On-Time</p>
                  {metrics.onTimeCompletionRate == null
                    ? <Badge variant="outline" className="mt-0.5 text-[9px] bg-muted text-muted-foreground">Not enough data</Badge>
                    : <p className="text-sm font-semibold tabular-nums mt-0.5">{Math.round(metrics.onTimeCompletionRate * 100)}%</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">Informational only. Crew metrics don't affect dispatch or tier.</p>
      </CardContent>
    </Card>
  );
}

export default function ContractorProfile() {
  const { id } = useParams<{ id: string }>();
  const { contractors, setContractors, opportunities, jobs, issues, credits, payProfiles, setPayProfiles, marketplaceOffers } = useAppData();
  const onboarding = useOnboarding();
  const [payFormOpen, setPayFormOpen] = useState(false);

  // Sync DB contractor into local state on mount so fields persisted to Supabase
  // (e.g. visibilityOverride, pay profile) are not wiped by seed data on refresh.
  useEffect(() => {
    if (!id) return;
    ContractorService.fetchContractorById(id).then((dbContractor) => {
      if (!dbContractor) return;
      setContractors((prev) => {
        const idx = prev.findIndex((c) => c.id === id);
        if (idx === -1) return [...prev, dbContractor];
        return prev.map((c) => (c.id === id ? { ...c, ...dbContractor } : c));
      });
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const contractor = contractors.find((c) => c.id === id);

  const aliasContractorIds = useMemo(() => {
    if (!contractor) return new Set<string>(id ? [id] : []);
    const targetName = normalizeIdentity(contractor.companyName || contractor.contactName);
    const ids = new Set<string>(id ? [id] : []);
    if (!targetName) return ids;
    contractors
      .filter((c) => normalizeIdentity(c.companyName || c.contactName) === targetName)
      .forEach((c) => ids.add(c.id));
    return ids;
  }, [contractor, contractors, id]);

  const canonicalMarketplaceOffers = useMemo(() => {
    if (!id) return marketplaceOffers;
    return marketplaceOffers.map((offer) =>
      aliasContractorIds.has(offer.contractorId) && offer.contractorId !== id
        ? { ...offer, contractorId: id }
        : offer,
    );
  }, [aliasContractorIds, id, marketplaceOffers]);

  const canonicalOpportunities = useMemo(() => {
    if (!id) return opportunities;
    return opportunities.map((opp) =>
      aliasContractorIds.has(opp.contractorId) && opp.contractorId !== id
        ? { ...opp, contractorId: id }
        : opp,
    );
  }, [aliasContractorIds, id, opportunities]);

  const contOpps = canonicalOpportunities.filter((o) => o.contractorId === id);
  const accepted = contOpps.filter((o) => o.responseStatus === "Accepted");
  const rejected = contOpps.filter((o) => o.responseStatus === "Rejected");
  const bids = contOpps.filter((o) => o.responseStatus === "Bid Submitted");
  const opportunityStatusByJobId = useMemo(() => {
    const out = new Map<string, "accepted" | "declined" | "expired" | "pending">();
    for (const opp of contOpps) {
      const mapped =
        opp.responseStatus === "Accepted"
          ? "accepted"
          : opp.responseStatus === "Rejected"
            ? "declined"
            : opp.responseStatus === "No Response"
              ? "expired"
              : "pending";
      const existing = out.get(opp.jobId);
      const rank = (s: string) =>
        s === "accepted" ? 4 :
        s === "declined" ? 3 :
        s === "expired" ? 2 : 1;
      if (!existing || rank(mapped) > rank(existing)) out.set(opp.jobId, mapped);
    }
    return out;
  }, [contOpps]);
  const offerHistoryRows = useMemo(() => {
    if (!id) return [] as Array<{
      id: string;
      jobId: string;
      sentAt: string;
      respondedAt?: string;
      status: string;
      amount?: number;
      isWinner: boolean;
    }>;
    return canonicalMarketplaceOffers
      .filter((offer) => offer.contractorId === id)
      .map((offer) => {
        const hasAcceptedAudit = offer.audit.some((a) => a.event === "offer_accepted");
        const hasDeclinedAudit = offer.audit.some((a) => a.event === "offer_declined");
        const hasExpiredAudit = offer.audit.some((a) => a.event === "offer_expired");
        const oppFallback = opportunityStatusByJobId.get(offer.jobId);
        const effectiveStatus = hasAcceptedAudit
          ? "accepted"
          : hasDeclinedAudit
            ? "declined"
            : hasExpiredAudit
              ? "expired"
              : offer.status === "pending" && oppFallback
                ? oppFallback
              : offer.status;
        return {
          id: offer.id,
          jobId: offer.jobId,
          sentAt: offer.sentAt,
          respondedAt: offer.respondedAt,
          status: effectiveStatus,
          amount: offer.payoutAmountSnapshot,
          isWinner: effectiveStatus === "accepted",
        };
      })
      .sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt));
  }, [canonicalMarketplaceOffers, id, opportunityStatusByJobId]);

  const assignedJobs = jobs.filter((j) =>
    !!contractor &&
    (
      j.assignedSubcontractorId === contractor.id ||
      (!j.assignedSubcontractorId && j.assignedSubcontractor === contractor.companyName)
    ),
  );
  const offerStatusByJobId = useMemo(() => {
    const byJob = new Map<string, string>();
    for (const offer of marketplaceOffers) {
      if (offer.contractorId !== contractor?.id) continue;
      const existing = byJob.get(offer.jobId);
      if (!existing) {
        byJob.set(offer.jobId, offer.status);
        continue;
      }
      const rank = (s: string) =>
        s === "accepted" ? 6 :
        s === "declined" ? 5 :
        s === "expired" ? 4 :
        s === "withdrawn" ? 3 :
        s === "superseded" ? 2 : 1;
      if (rank(offer.status) > rank(existing)) byJob.set(offer.jobId, offer.status);
    }
    return byJob;
  }, [marketplaceOffers, contractor?.id]);
  const jobById = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);
  const activeJobs = assignedJobs.filter((j) => getEffectiveStage(j, offerStatusByJobId) !== "Completed");
  const completedJobs = assignedJobs.filter((j) => getEffectiveStage(j, offerStatusByJobId) === "Completed");
  const jobIssues = issues.filter((i) => assignedJobs.some((j) => j.id === i.jobId));
  const openIssues = jobIssues.filter((i) => i.status === "Open" || i.status === "In Progress");
  const sortedJobIssues = useMemo(() => {
    const priority = (status: string) => {
      if (status === "Open") return 0;
      if (status === "In Progress") return 1;
      return 2;
    };
    const toTime = (value?: string) => {
      if (!value) return 0;
      const ms = Date.parse(value);
      return Number.isNaN(ms) ? 0 : ms;
    };
    return [...jobIssues].sort((a, b) => {
      const p = priority(a.status) - priority(b.status);
      if (p !== 0) return p;
      return toTime(b.dateCreated) - toTime(a.dateCreated);
    });
  }, [jobIssues]);

  const responseRate = contOpps.length > 0
    ? Math.round(((accepted.length + rejected.length + bids.length) / contOpps.length) * 100)
    : 0;
  const headerScorecard = useMemo(
    () => computeScorecard(contractor, canonicalOpportunities, jobs, issues, credits, canonicalMarketplaceOffers),
    [contractor, canonicalOpportunities, jobs, issues, credits, canonicalMarketplaceOffers],
  );

  const parentContractor = useMemo(
    () =>
      contractor?.parentEntityId
        ? contractors.find((candidate) => candidate.id === contractor.parentEntityId)
        : undefined,
    [contractor?.parentEntityId, contractors],
  );
  const latestOnboardingSession = useMemo(() => {
    if (!contractor) return undefined;
    return onboarding.sessions
      .filter((session) => session.contractorId === contractor.id)
      .sort((a, b) => Date.parse(b.lastTouchedAt) - Date.parse(a.lastTouchedAt))[0];
  }, [contractor, onboarding.sessions]);
  const hasDbBackedComplianceEvidence = useMemo(() => {
    if (!contractor) return false;
    return (
      onboarding.complianceDocuments.some((doc) => doc.contractorId === contractor.id) ||
      Boolean(latestOnboardingSession)
    );
  }, [contractor, latestOnboardingSession, onboarding.complianceDocuments]);
  const complianceRows = useMemo(() => {
    if (!contractor) return [];
    if (!hasDbBackedComplianceEvidence) {
      return [
        {
          requirement: "insurance" as ComplianceRequirementKey,
          label: "Insurance",
          status:
            contractor.insuranceOnFile && isInsuranceCurrent(contractor.insuranceExpiry)
              ? "ok"
              : "missing",
          detail: contractor.insuranceExpiry ? `Expires ${contractor.insuranceExpiry}` : undefined,
        },
        {
          requirement: "w9" as ComplianceRequirementKey,
          label: "W-9",
          status: contractor.w9OnFile ? "ok" : "missing",
        },
        {
          requirement: "agreement" as ComplianceRequirementKey,
          label: "Agreement",
          status: contractor.agreementSigned ? "ok" : "missing",
        },
      ] as Array<{
        requirement: ComplianceRequirementKey;
        label: string;
        status: RequirementStatus;
        detail?: string;
      }>;
    }

    return getComplianceStatus(contractor, onboarding.complianceDocuments, parentContractor)
      .requirements
      .filter((item) => item.requirement !== "banking_verification")
      .filter((item) => item.required || item.status !== "not_required")
      .map((item) => {
        const status =
          item.requirement === "agreement" &&
          item.status !== "ok" &&
          latestOnboardingSession &&
          hasRequiredAgreementSignature(latestOnboardingSession)
            ? "ok"
            : item.status;
        return {
          requirement: item.requirement,
          label: getRequirementLabel(item.requirement),
          status,
          detail: getRequirementDetail(item),
        };
      });
  }, [
    contractor,
    hasDbBackedComplianceEvidence,
    latestOnboardingSession,
    onboarding.complianceDocuments,
    parentContractor,
  ]);

  if (!contractor) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Contractor not found.</p>
        <Link to="/contractors" className="text-primary hover:underline text-sm mt-2 inline-block">Back to Directory</Link>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-5 max-w-4xl">
      <Link to="/contractors" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Directory
      </Link>

      {/* Header card */}
      <Card className="shadow-none">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-full flex items-center justify-center shrink-0 ${
              contractor.active ? "bg-success/10" : "bg-muted"
            }`}>
              <Users className={`h-6 w-6 ${contractor.active ? "text-success" : "text-muted-foreground"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold tracking-tight">{contractor.companyName}</h1>
                <ReliabilityBadge scorecard={headerScorecard} size="md" />
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  contractor.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
                }`}>
                  {contractor.active ? "Active" : "Inactive"}
                </span>
                {contractor.active && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    contractor.availabilityStatus === "Available"
                      ? "bg-success/15 text-success"
                      : contractor.availabilityStatus === "Limited"
                      ? "bg-warning/15 text-warning"
                      : "bg-destructive/15 text-destructive"
                  }`}>
                    {contractor.availabilityStatus}
                    {contractor.maxWeeklyJobs > 0 && ` · ${contractor.maxWeeklyJobs} jobs/wk`}
                  </span>
                )}
                <StatusBadge type="stage" value={contractor.onboardingStatus as any} className="text-[10px]" />
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{contractor.contactName}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {contractor.phone}</span>
                <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {contractor.email}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {contractor.serviceArea}</span>
                <span className="inline-flex items-center gap-1"><Wrench className="h-3 w-3" /> {contractor.tradeType}</span>
              </div>
              <div className="mt-1.5">
                <Link
                  to={`/admin/installer-organizations?focus=${contractor.id}`}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                >
                  <Users className="h-3 w-3" />
                  View crew roster
                </Link>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Quality</p>
                  <RatingStars rating={contractor.rating} />
                </div>
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Responsiveness</p>
                  <RatingStars rating={contractor.responsivenessRating} />
                </div>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
            {[
              { label: "Opportunities", value: headerScorecard.totalOpps },
              { label: "Accepted", value: headerScorecard.accepted },
              { label: "Bids", value: headerScorecard.bids },
              { label: "Won", value: headerScorecard.accepted },
              { label: "Response Rate", value: `${headerScorecard.responseRate}%` },
              { label: "Open Issues", value: headerScorecard.openIssues },
            ].map((kpi) => (
              <div key={kpi.label} className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-lg font-bold">{kpi.value}</p>
                <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">{kpi.label}</p>
              </div>
            ))}
          </div>

          {/* Tags */}
          {contractor.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {contractor.tags.map((tag) => (
                <span key={tag} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pay Profile — required before assignment */}
      <PayProfileCard
        profile={getPayProfile(payProfiles, contractor.id)}
        onEdit={() => setPayFormOpen(true)}
        onCreate={() => setPayFormOpen(true)}
      />

      {/* Marketplace eligibility (P0 — read-only + admin override) */}
      <ContractorMarketplacePanel contractor={contractor} />

      {/* Offer accountability history (P2 — read-only metrics + history) */}
      <ContractorOfferHistoryPanel contractorId={contractor.id} />

      {/* Performance Scorecard */}
      <ContractorScorecardFull scorecard={computeScorecard(contractor, canonicalOpportunities, jobs, issues, credits, canonicalMarketplaceOffers)} />

      {/* Crews */}
      <ContractorCrewsSection contractorId={contractor.id} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Compliance */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <Shield className="h-4 w-4" /> Compliance & Insurance
            </h2>
            <div className="space-y-2">
              {complianceRows.map((item) => (
                <div key={item.requirement} className="flex items-center justify-between gap-3 py-1.5">
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    {item.detail && (
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {item.detail}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {getRequirementStatusIcon(item.status)}
                    <span className={`text-[10px] font-medium ${getRequirementStatusTone(item.status)}`}>
                      {getRequirementStatusLabel(item.status)}
                    </span>
                  </div>
                </div>
              ))}
              {contractor.licenseNumber && (
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-muted-foreground">License</span>
                  <span className="text-xs font-mono">{contractor.licenseNumber}</span>
                </div>
              )}
              {contractor.complianceNotes && (
                <div className="mt-2 p-2.5 rounded-lg bg-muted/20 border">
                  <p className="text-[10px] text-muted-foreground">{contractor.complianceNotes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" /> Details
            </h2>
            <div className="space-y-1.5">
              {[
                { label: "Branch", value: contractor.branch },
                { label: "Service Regions", value: contractor.serviceRegions.join(", ") },
                { label: "Availability", value: contractor.availabilityStatus },
                { label: "Capacity", value: contractor.maxWeeklyJobs > 0 ? `${contractor.maxWeeklyJobs} jobs/week` : "—" },
                { label: "Joined", value: formatDisplayDateTime(contractor.joinedDate) },
                { label: "Onboarding", value: contractor.onboardingStatus },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-muted-foreground">{row.label}</span>
                  <span className="text-xs font-medium">{row.value}</span>
                </div>
              ))}
              {contractor.serviceZipCodes.length > 0 && (
                <div className="pt-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1.5">Service Zip Codes</p>
                  <div className="flex flex-wrap gap-1">
                    {contractor.serviceZipCodes.map((z) => (
                      <span key={z} className="text-[10px] bg-muted/60 text-muted-foreground rounded px-1.5 py-0.5 font-mono">{z}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {contractor.notes && (
              <div className="mt-3 p-2.5 rounded-lg bg-muted/20 border">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1">Notes</p>
                <p className="text-xs text-muted-foreground">{contractor.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Jobs */}
      {activeJobs.length > 0 && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <Briefcase className="h-4 w-4" /> Active Jobs ({activeJobs.length})
            </h2>
            <div className="space-y-2">
              {activeJobs.map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="block">
                  <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-xs text-primary">{getJobDisplayRef(job)}</span>
                      <span className="text-xs font-medium">{job.customerName}</span>
                      <span className="text-xs text-muted-foreground hidden sm:inline">· {job.projectType}</span>
                    </div>
                    <StatusBadge type="stage" value={getEffectiveStage(job, offerStatusByJobId)} />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Jobs */}
      {completedJobs.length > 0 && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-success" /> Completed Jobs ({completedJobs.length})
            </h2>
            <div className="space-y-2">
              {completedJobs.map((job) => (
                <Link key={job.id} to={`/jobs/${job.id}`} className="block">
                  <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/10 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-xs text-primary">{getJobDisplayRef(job)}</span>
                      <span className="text-xs font-medium">{job.customerName}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">${job.payoutAmount.toLocaleString()}</span>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Response History */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
            <Send className="h-4 w-4" /> Opportunity Response History ({offerHistoryRows.length})
          </h2>
          {offerHistoryRows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
              No opportunity response history found for this contractor.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Job</th>
                    <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Sent</th>
                    <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Viewed</th>
                    <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Response</th>
                    <th className="text-right py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Bid</th>
                    <th className="text-center py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Won</th>
                  </tr>
                </thead>
                <tbody>
                  {offerHistoryRows.map((row) => {
                    const job = jobById.get(row.jobId);
                    return (
                      <tr key={row.id} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-3">
                          <Link to={`/jobs/${row.jobId}`} className="font-mono text-primary hover:underline">{job ? getJobDisplayRef(job) : row.jobId}</Link>
                          {job && <span className="text-muted-foreground ml-1.5 hidden sm:inline">{job.customerName}</span>}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{new Date(row.sentAt).toLocaleDateString()}</td>
                        <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{row.respondedAt ? new Date(row.respondedAt).toLocaleDateString() : "—"}</td>
                        <td className="py-2 px-3">
                          <Badge variant="outline" className="capitalize">{row.status}</Badge>
                        </td>
                        <td className="py-2 px-3 text-right hidden sm:table-cell">
                          {typeof row.amount === "number" ? `$${row.amount.toLocaleString()}` : "—"}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {row.isWinner ? <CheckCircle className="h-4 w-4 text-success mx-auto" /> : <span className="text-muted-foreground">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Issues */}
      <Card className="shadow-none">
        <CardContent className="p-4">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Issue History ({jobIssues.length})
          </h2>
          {jobIssues.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground">
              No issue history found for this contractor.
            </div>
          ) : (
            <div className="space-y-2">
              {sortedJobIssues.map((i) => {
                const issueJob = jobById.get(i.jobId);
                return (
                <div key={i.id} className={`p-3 rounded-lg border ${
                  i.status === "Open" || i.status === "In Progress" ? "border-destructive/20 bg-destructive/[0.03]" : "bg-muted/20"
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <Link to={`/jobs/${i.jobId}`} className="font-mono text-[10px] text-primary hover:underline">
                        {issueJob ? getJobDisplayRef(issueJob) : i.jobId}
                      </Link>
                      <span className="font-semibold text-xs">{i.issueType}</span>
                    </div>
                    <div className="flex gap-1.5">
                      <StatusBadge type="severity" value={i.severity} />
                      <StatusBadge type="issueStatus" value={i.status} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{i.description}</p>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    <PayProfileForm
      open={payFormOpen}
      onOpenChange={setPayFormOpen}
      contractorId={contractor.id}
      contractorName={contractor.companyName}
      contractor={contractor}
      existing={getPayProfile(payProfiles, contractor.id)}
      onSave={async (profile, contractorPatch) => {
        if (contractorPatch && Object.keys(contractorPatch).length > 0) {
          const updatedContractor = await ContractorService.updateContractor(contractor.id, contractorPatch);
          if (!updatedContractor) {
            toast.error("Failed to save installer service scope");
            return;
          }
          setContractors((prev) => prev.map((c) => (c.id === contractor.id ? { ...c, ...updatedContractor } : c)));
        }

        const { error } = await ContractorService.savePayProfile(profile.contractorId, profile);
        if (error) {
          console.warn("[PayProfile] Failed to save to DB:", error);
          toast.error("Failed to save pay profile");
          return;
        }
        setPayProfiles((prev) => {
          const idx = prev.findIndex((p) => p.contractorId === profile.contractorId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = profile;
            return next;
          }
          return [...prev, profile];
        });
        toast.success("Installer eligibility profile saved");
      }}
    />
    </>
  );
}
